import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, MessageCircle, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

export default function AIChat() {
  const { t, tx, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "ai", text: t.ai.welcome, recommendations: [] }]);
    }
  }, [open, t.ai.welcome, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages(m => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/ai/ask", { message: text, language: lang, session_id: sessionId });
      if (!sessionId) setSessionId(r.data.session_id);
      setMessages(m => [...m, {
        role: "ai",
        text: r.data.respuesta,
        recommendations: r.data.lugares_recomendados || [],
        avisos: r.data.avisos || [],
      }]);
    } catch (e) {
      setMessages(m => [...m, { role: "ai", text: e?.response?.data?.detail || "Error", error: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-ai-fab"
          className="fixed bottom-20 right-5 md:bottom-8 md:right-8 z-40 group"
          aria-label="Ask AI"
        >
          <span className="relative flex items-center gap-2 px-5 py-4 rounded-full bg-[hsl(var(--primary-red))] text-white shadow-2xl hover:bg-[hsl(var(--primary-red-hover))] transition">
            <span className="absolute inset-0 rounded-full pulse-ring opacity-60" />
            <Sparkles className="w-5 h-5 relative" />
            <span className="hidden sm:inline relative font-semibold">{t.hero.askAI}</span>
            <MessageCircle className="w-5 h-5 sm:hidden relative" />
          </span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:justify-end p-0 md:p-6 bg-black/40 backdrop-blur-sm" data-testid="ai-chat-drawer">
          <div className="w-full md:w-[420px] h-[85vh] md:h-[640px] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-fade-up">
            {/* Header */}
            <div className="px-5 py-4 bg-[hsl(var(--primary-red))] text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <h3 className="font-display font-semibold">{t.ai.title}</h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="hover:bg-white/20 p-1.5 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin bg-muted/30">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "space-y-2"}`}>
                    <div className={`px-4 py-3 ${
                      m.role === "user"
                        ? "bg-white border border-border rounded-2xl rounded-br-sm"
                        : "bg-[hsl(var(--primary-red))] text-white rounded-2xl rounded-bl-sm"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                    </div>
                    {m.recommendations && m.recommendations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground px-1">{t.ai.recommended}</p>
                        {m.recommendations.map((r, idx) => (
                          <Link
                            key={idx}
                            to={`/places/${r.slug}`}
                            onClick={() => setOpen(false)}
                            data-testid={`ai-rec-${idx}`}
                            className="flex items-center gap-3 p-2.5 bg-white border border-border rounded-xl hover:shadow-md hover:border-[hsl(var(--primary-green))] transition"
                          >
                            {r.image_url ? (
                              <img src={r.image_url} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold truncate">{tx(r.title)}</p>
                              <p className="text-xs text-muted-foreground truncate">{tx(r.short_description) || r.address}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                    {m.avisos && m.avisos.length > 0 && (
                      <div className="space-y-1">
                        {m.avisos.map((a, idx) => (
                          <p key={idx} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">⚠ {a}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 bg-[hsl(var(--primary-red))]/80 text-white rounded-2xl rounded-bl-sm">
                    <span className="inline-flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce" />
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: "0.1s" }} />
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: "0.2s" }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-3 py-3 border-t border-border bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t.ai.placeholder}
                  rows={1}
                  data-testid="ai-chat-input"
                  className="flex-1 resize-none px-4 py-3 text-sm bg-muted rounded-2xl outline-none focus:ring-2 ring-[hsl(var(--primary-red))]"
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="ai-chat-send"
                  className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary-red))] text-white flex items-center justify-center hover:bg-[hsl(var(--primary-red-hover))] disabled:opacity-40 disabled:cursor-not-allowed transition"
                  aria-label="send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
